import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useCallback } from 'react';
import { z } from 'zod';

import { OfferPage } from '~/components/offer/OfferPage';
import { useAnalytics } from '~/hooks/useAnalytics';
import type { PublicLandingPage } from '~/types';

/**
 * Legacy /$slug/offer route (no episodeId in the path).
 *
 * This route never resolves per-episode overrides — without an episodeId we
 * cannot look up an override anyway. It renders `offerContent`/`forwardUrl`/
 * `showYakOffer` directly from the by-slug payload and follows the
 * R1-M2 no-sessionRef CTA contract (plain `<a target="_blank"
 * rel="noopener noreferrer">`).
 *
 * Behavior changes (third-loop):
 * - No /v1/public/landing-pages/offer call (R5-H1 — feature gate is
 *   irrelevant; the call would have nothing to resolve).
 * - No /v1/public/claim/:sessionRef call (R1-M2 — no token, no anchored
 *   claim record).
 * - Referrer-Policy: no-referrer is applied via route `head()` (R1-H1).
 */
export const Route = createFileRoute('/$slug/offer')({
  validateSearch: z.object({
    sessionRef: z.string().optional(),
  }),
  head: () => ({
    meta: [{ name: 'referrer', content: 'no-referrer' }],
  }),
  component: OfferRoute,
});

function OfferRoute() {
  const router = useRouter();

  const { slug, landingPage } = Route.useRouteContext() as {
    slug: string;
    landingPage: PublicLandingPage;
  };
  const { trackEvent } = useAnalytics({ slug });

  const handleForwardClick = useCallback(() => {
    trackEvent('offer_click');
  }, [trackEvent]);

  const handlePlayAgain = useCallback(() => {
    if (landingPage.targetType === 'show') {
      router.navigate({ to: '/$slug/episodes', params: { slug } });
    } else {
      router.navigate({
        to: '/$slug/game/$episodeId',
        params: { slug, episodeId: landingPage.targetId },
      });
    }
  }, [router, slug, landingPage]);

  const handleSkip = useCallback(() => {
    router.navigate({ to: '/$slug/episodes', params: { slug } });
  }, [router, slug]);

  return (
    <OfferPage
      offerContent={landingPage.offerContent}
      branding={landingPage.branding}
      forwardUrl={landingPage.forwardUrl}
      title={landingPage.title}
      onForwardClick={handleForwardClick}
      onOfferView={() => trackEvent('offer_view')}
      onSkip={handleSkip}
      targetType={landingPage.targetType}
      onPlayAgain={handlePlayAgain}
      showYakOffer={landingPage.showYakOffer}
      useDirectClaimAnchor
    />
  );
}
